import React, { useState } from 'react';
import QuickbooksAccountMapping from './QuickbooksAccountMapping';

const QuickbooksConnect = ({
  quickbooks_connected,
  quickbooks_last_synced_at,
  quickbooks_connect_url,
  quickbooks_disconnect_url,
  quickbooks_sync_url,
  quickbooks_accounts_url,
  quickbooks_account_mappings_url,
  quickbooks_expense_accounts_url,
  quickbooks_expense_mappings_url,
  quickbooks_expense_categories,
  features,
}) => {
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const handleSync = async (e) => {
    e.preventDefault();
    setSyncing(true);
    setSyncMessage(null);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(quickbooks_sync_url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (response.ok) {
        setSyncMessage('Sync started — your P&L data will be updated shortly.');
      } else {
        setSyncMessage(data.error || 'Sync failed. Please try again.');
      }
    } catch {
      setSyncMessage('Sync failed. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const formattedDate = quickbooks_last_synced_at
    ? new Date(quickbooks_last_synced_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="min-h-screen bg-succession-bg-primary p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-semibold text-succession-text-primary mb-2">
          Integrations
        </h1>
        <p className="text-succession-text-secondary mb-8">
          Connect third-party services to automatically import data into your valuation profile.
        </p>

        <div className="bg-succession-bg-card rounded-xl border border-succession-border-primary p-6">
          <div className="flex items-start gap-4">
            <img
              src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
              alt="QuickBooks"
              className="w-12 h-12 flex-shrink-0 rounded-lg object-contain"
            />

            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-base font-semibold text-succession-text-primary">
                    QuickBooks Online
                  </h2>
                  <p className="text-sm text-succession-text-secondary mt-0.5">
                    Import total revenue, operating expenses, and up to 5 years of gross production.
                  </p>
                </div>

                {quickbooks_connected ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Not connected
                  </span>
                )}
              </div>

              {quickbooks_connected && formattedDate && (
                <p className="text-xs text-succession-text-tertiary mt-2">
                  Last synced: {formattedDate}
                </p>
              )}

              {syncMessage && (
                <p className="text-sm text-succession-text-secondary mt-3 bg-succession-bg-secondary rounded-lg px-3 py-2">
                  {syncMessage}
                </p>
              )}

              <div className="flex flex-wrap gap-3 mt-4">
                {quickbooks_connected ? (
                  <>
                    <button
                      onClick={handleSync}
                      disabled={syncing}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-succession-accent text-white hover:bg-succession-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {syncing ? 'Syncing…' : 'Sync Latest Data'}
                    </button>

                    <form
                      action={quickbooks_disconnect_url}
                      method="post"
                      onSubmit={(e) => {
                        if (!window.confirm('Disconnect QuickBooks? Imported values will remain but will no longer auto-update.')) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <input
                        type="hidden"
                        name="_method"
                        value="delete"
                      />
                      <input
                        type="hidden"
                        name="authenticity_token"
                        value={document.querySelector('meta[name="csrf-token"]')?.content}
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Disconnect
                      </button>
                    </form>
                  </>
                ) : (
                  <a
                    href={quickbooks_connect_url}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-succession-accent text-white hover:bg-succession-accent-hover transition-colors"
                  >
                    Connect QuickBooks
                  </a>
                )}
              </div>

              {quickbooks_connected && quickbooks_accounts_url && (
                <QuickbooksAccountMapping
                  quickbooks_accounts_url={quickbooks_accounts_url}
                  quickbooks_account_mappings_url={quickbooks_account_mappings_url}
                  quickbooks_expense_accounts_url={quickbooks_expense_accounts_url}
                  quickbooks_expense_mappings_url={quickbooks_expense_mappings_url}
                  quickbooks_expense_categories={quickbooks_expense_categories}
                  features={features}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickbooksConnect;
